import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, defer, Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SpinnerService } from 'is-lib';
import { ServerResponse } from '../models/ServerResponse';
import { SnackBarService } from './snackbar.service';
import { SnackBarType } from '../models/Snackbar';
import { CACHE_EXPIRATION_SUBJECT, RequestCacheConfig } from '../models/Request';

type HeadersInput = HttpHeaders | { [header: string]: string | string[] };

interface RequestOptions {
	headers?: HeadersInput;
	[option: string]: any;
}

@Injectable({
	providedIn: 'root'
})
export class GenericService {
	public appInitialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private _http: HttpClient,
		private _snackbar: SnackBarService,
		private _spinnerService: SpinnerService
	) {
	}

	/** Read on every request so a country change after login is picked up. */
	get selectedSIGA(): string | null {
		return localStorage.getItem('selectedSIGA');
	}

	//#region HTTP methods

	get<T>(url: string, loaderConfig: LoaderConfig | boolean = false, params?: any, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<ServerResponse<T>> {
		const headers = this.buildHeaders(undefined, requestCacheConfig, { 'Accept-Language': this.selectedSIGA ?? 'en-US' });

		return this.request(this._http.get<ServerResponse<T>>(url, { headers, params: params ?? undefined, context: this.buildContext(undefined, requestCacheConfig) }), loaderConfig);
	}

	post(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<any> {
		return this.request(this._http.post(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig);
	}

	put(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<any> {
		return this.request(this._http.put(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig, true);
	}

	patch(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<any> {
		return this.request(this._http.patch(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig, true);
	}

	delete<T>(url: string, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig()): Observable<ServerResponse<T>> {
		const headers = this.buildHeaders(undefined, requestCacheConfig);

		return this.request(this._http.delete<ServerResponse<T>>(url, { headers, context: this.buildContext(undefined, requestCacheConfig) }), loaderConfig);
	}

	//#endregion

	//#region Helpers

	/**
	 * Wraps a (cold) HttpClient observable so the loader is shown on subscribe and hidden on
	 * completion/error/unsubscribe. Optionally shows an error snackbar.
	 */
	private request<R>(request$: Observable<R>, loaderConfig: LoaderConfig | boolean, notifyOnError: boolean = false): Observable<R> {
		const loader = LoaderConfig.from(loaderConfig);

		return defer(() => {
			this.showLoader(loader);
			return request$;
		}).pipe(
			catchError((err: HttpErrorResponse) => {
				if (notifyOnError) {
					this.showErrorSnackbar(err);
				}
				return throwError(() => err);
			}),
			finalize(() => this.hideLoader(loader))
		);
	}

	/** Accepts either an options object or a bare HttpHeaders (legacy call style) and adds the cache header. */
	private buildOptions(options: RequestOptions | HttpHeaders | undefined, requestCacheConfig: RequestCacheConfig): RequestOptions & { headers: HttpHeaders } {
		const normalized: RequestOptions = options instanceof HttpHeaders ? { headers: options } : { ...options };

		return { ...normalized, headers: this.buildHeaders(normalized.headers, requestCacheConfig), context: this.buildContext(normalized['context'], requestCacheConfig) };
	}

	private buildContext(context: HttpContext | undefined, requestCacheConfig: RequestCacheConfig): HttpContext {
		return (context ?? new HttpContext()).set(CACHE_EXPIRATION_SUBJECT, requestCacheConfig.expirationSubject$);
	}

	private buildHeaders(headers: HeadersInput | undefined, requestCacheConfig: RequestCacheConfig, extra: { [header: string]: string } = {}): HttpHeaders {
		let result = headers instanceof HttpHeaders ? headers : new HttpHeaders(headers ?? {});

		const toSet = { ...extra, 'ExpirationTimer': requestCacheConfig.persistenceTimerInMs.toString() };
		for (const [name, value] of Object.entries(toSet)) {
			result = result.set(name, value);
		}

		return result;
	}

	private showErrorSnackbar(err: HttpErrorResponse) {
		const statusCode = err.error?.statusCode;
		const message = err.error?.message;

		if (statusCode && !String.isNullOrUndefinedOrWhitespace(message)) {
			this._snackbar.callSnackBar(`${statusCode} - ${message}`, SnackBarType.ERROR);
		} else {
			this._snackbar.callSnackBar('ERROR_OCURRED', SnackBarType.ERROR);
		}
	}

	protected showLoader(loaderConfig: LoaderConfig) {
		if (!loaderConfig.show) {
			return;
		}

		if (String.isNullOrUndefinedOrWhitespace(loaderConfig.identifierName)) {
			this._spinnerService.showFullscreenSpinner();
		} else {
			this._spinnerService.showInComponentSpinner(loaderConfig.identifierName!);
		}
	}

	protected hideLoader(loaderConfig: LoaderConfig) {
		if (!loaderConfig.show) {
			return;
		}

		if (String.isNullOrUndefinedOrWhitespace(loaderConfig.identifierName)) {
			this._spinnerService.hideFullscreenSpinner();
		} else {
			this._spinnerService.hideInComponentSpinner(loaderConfig.identifierName!);
		}
	}

	//#endregion
}

export class LoaderConfig {
	show: boolean;
	fullscreen: boolean;
	identifierName?: string;

	constructor(show: boolean, fullscreen: boolean, identifierName?: string) {
		this.show = show;
		this.fullscreen = fullscreen;
		this.identifierName = identifierName;
	}

	static from(loaderConfig: LoaderConfig | boolean): LoaderConfig {
		return typeof loaderConfig === 'boolean' ? new LoaderConfig(loaderConfig, false) : loaderConfig;
	}
}
